import { Form } from 'antd';
import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import { I_Category, I_CheckoutCartItem } from 'app/pages/Cart/slice/types';
import {
  MATERRIAL_EVOUCHER_TYPE,
  MATERRIAL_PARK_TYPE,
  MATERRIAL_SHOW_TYPE,
} from 'app/pages/Checkout/hooks/useCheckout';
import { CheckoutDetail } from 'app/pages/Checkout/slice/types';
import { OrderState } from 'app/pages/NewAccount/types';
import {
  getCheckoutDetailOrderApi,
  updateCheckoutPaymentApi,
} from 'app/services/checkoutApi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { I_Park, objectType } from 'types';
import { generateUUID } from 'utils/helper';
import { useCheckoutSlice } from '../slice';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import { debounce } from 'lodash';
import { AxiosError } from 'axios';
import { flushSync } from 'react-dom';
import { SYSTEM_MAINTENANCE } from 'utils/constants';

interface Props {
  paymentMethodActive?: number;
  onTriggerSubmit?: () => void;
}

export type GroupDatas = {
  park: I_Park;
  catalogs: Record<
    string,
    {
      catalog: any;
      items: I_CheckoutCartItem[];
    }
  >;
};

const groupItems = (
  items: I_CheckoutCartItem[],
): Record<
  string,
  {
    park: I_Park;
    catalogs: Record<string, { catalog: any; items: I_CheckoutCartItem[] }>;
  }
> => {
  return items.reduce((groupedItems, item) => {
    const parkKey = `${item.park.id}`;
    const catalogKey = `${item.catalog.id}`;

    if (!groupedItems[parkKey]) {
      groupedItems[parkKey] = { park: item.park, catalogs: {} };
    }

    if (!groupedItems[parkKey].catalogs[catalogKey]) {
      groupedItems[parkKey].catalogs[catalogKey] = {
        catalog: item.catalog,
        items: [],
      };
    }

    groupedItems[parkKey].catalogs[catalogKey].items.push(item);

    return groupedItems;
  }, {} as Record<string, { park: I_Park; catalogs: Record<string, { catalog: any; items: I_CheckoutCartItem[] }> }>);
};

export const useUpdateTicketInfo = () => {
  useCheckoutSlice();
  const [formInfoUser] = Form.useForm();
  const [listCart, setListCart] = useState<I_CheckoutCartItem[]>([]);
  const [vouchers, setVouchers] = useState<Partial<I_CheckoutCartItem>[]>([]);
  const [groupCart, setGroupCart] = useState<GroupDatas[]>([]);
  const [orderState, setOrderState] = useState<OrderState>();
  // TODO: change type
  const [mailTaxs, setMailTaxs] = useState<string[]>([]);
  const [representativesInfo, setRepresentativesInfo] = useState<any>([]);
  const [businessPartner, setBusinessPartner] = useState<any>([]);
  const [orderDetail, setOrderDetail] = useState<Record<string, any>>();

  const location = useLocation();
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();

  const [loading, setLoading] = useState(false);
  const [enableConfirm, setEnableConfirm] = useState(false);
  const [idOrder, setIdOrder] = useState<number>();
  const [mailReceiveInvoices, setMailReceiveInvoices] = useState<
    { id: string; initValue: string }[]
  >([]);
  const orderCheckout = useRef<CheckoutDetail>();
  const isMountDataOrder = useRef(false);
  const translations = useSelector(selectTranslations);
  const history = useHistory();

  const infoUser = useSelector(selectInfoUser);
  // const datesSelectedToCheckout = useSelector(selectDatesSelectedToCheckout);
  const INIT_FORM = useMemo(
    () => ({
      name: infoUser?.name || '',
      phone: infoUser?.phone || '',
      email: infoUser?.email || '',
      taxCode: infoUser?.businessPartner?.tax || '',
      companyName: infoUser?.businessPartner?.fullName || '',
      companyAddress: infoUser?.businessPartner?.address || '',
    }),
    [infoUser],
  );

  const totalAmount = useMemo(
    () => listCart.reduce((result, item) => result + +item.amount, 0),
    [listCart],
  );

  const [totalPrice, setTotalPrice] = useState(0);

  const [totalPriceOriginal, setTotalPriceOriginal] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);

  const listTicket = useMemo(() => {
    return listCart.filter(item => !item.isGift);
  }, [listCart]);

  const getFormUpdateValue = useCallback(
    (values: objectType) => {
      const formData = new FormData();

      Object.entries(values).forEach(([key, value]) => {
        if (
          key === 'taxCode' ||
          key === 'companyName' ||
          key === 'companyAddress' ||
          key === 'mailCompany'
        ) {
          return;
        }
        formData.append(key, value ?? '');
      });

      const mailValues = mailReceiveInvoices.map((item, index) => {
        formData.delete(`emailTax[${item.id}]`);
        return {
          email: values[`emailTax[${item.id}]`] as string | undefined,
          id: `emailTax[${item.id}]`,
        };
      });
      mailValues.forEach((item, index) => {
        formData.append(`emailTax[${index}]`, item.email ?? '');
      });
      formData.append('idHash', `${orderCheckout.current?.idHash ?? ''}`);
      return formData;
    },
    [mailReceiveInvoices],
  );

  const onSubmitForm = useCallback(
    async (values: objectType) => {
      try {
        setLoading(true);
        const formData = getFormUpdateValue(values);
        const { data } = await updateCheckoutPaymentApi(formData);
        if ((data?.errorField?.length ?? 0) > 0) {
          data?.errorField.forEach(item => {
            const err =
              typeof item.message === 'string'
                ? item.message
                : item.message?.[Object.keys(item.message)?.[0]];

            formInfoUser.setFields([
              {
                name: item.field,
                errors: [err],
              },
            ]);
          });
          return;
        }
        setEnableConfirm(false);
        onOpenDialogNoti({
          type: 'SUCCESS',
          isShow: true,
          title: translations?.['b2b.txt.update-info-success'] ?? '',
          content: '',
          closable: false,
          buttons: [
            {
              text: translations?.['b2b.btn.confirm'] ?? '',
              bg: 'linear-gradient(79.97deg, #F06F24 -7.79%, #F8B117 107.61%)',
              color: 'white',
              onClick: () => {
                onCloseDialogNoti();
                history.goBack();
              },
            },
          ],
          maskClosable: false,
        });
      } catch (error: any) {
        if (error === SYSTEM_MAINTENANCE) {
          setEnableConfirm(false);
          return;
        }
        const errCode = error?.data?.code;
        if (errCode === 'MSG022') {
          onOpenDialogNoti({
            type: 'ERROR',
            isShow: true,
            title: error?.message ?? '',
            content: '',
            onCloseDialog: () => {
              onCloseDialogNoti();
              flushSync(() => {
                setEnableConfirm(false);
              });
              history.push('/account', {
                targetTab: 'tab-3',
              });
            },
            buttons: [],
            maskClosable: false,
            closable: true,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [
      formInfoUser,
      getFormUpdateValue,
      onCloseDialogNoti,
      onOpenDialogNoti,
      translations,
    ],
  );
  useEffect(() => {
    const getDetail = async () => {
      try {
        setLoading(true);
        const search = new URLSearchParams(location.search);
        const orderId = search.get('order_id');
        if (!orderId) {
          history.goBack();
          return;
        }
        const { data: detailOrder } = await getCheckoutDetailOrderApi(orderId);
        setOrderDetail(detailOrder);
        setRepresentativesInfo(detailOrder.representativesInfo);
        setBusinessPartner(detailOrder?.agent?.businessPartner)
        setMailTaxs(detailOrder?.mailTaxs || []);

        setEnableConfirm(true);
        orderCheckout.current = detailOrder;
        setTotalDiscount(detailOrder.totalDiscount);
        setTotalPrice(detailOrder.totalPrice);
        setTotalPriceOriginal(detailOrder.totalOriginalPrice);
        setIdOrder(detailOrder.id);
        setOrderState(detailOrder.orderState.value);

        const items: I_CheckoutCartItem[] = detailOrder?.items.map(item => ({
          ...item,
          product: {
            id: item.id,
            name: item.productName,
            price: item.price,
            originalPrice: item.originalPrice,
            categories: [] as I_Category[],
            amount: `${item.amount}`,
            parks: [item.park],
            park: item.park,
            image: item.park.image,
            versionB2b: item?.product?.versionB2b,
            usedArea2Object: item?.product?.usedArea2Object,
          },
          amount: `${item.amount}`,
          date: item.date.dateDMY,
          zone: item.zone,
          show: item.show,
          price: item.price,
          originalPrice: item.originalPrice,
          performance: { ...item.performance, show: item.show },
          park: item.park,
          catalog: item.catalog,
          isGift: false,
          selectedDate: item.selectedDate?.dateDMY ?? '',
        }));
        const giftItems: I_CheckoutCartItem[] =
          detailOrder?.giftItems
            ?.filter(
              val =>
                val.materialType === MATERRIAL_SHOW_TYPE ||
                val.materialType === MATERRIAL_PARK_TYPE,
            )
            .map(item => ({
              ...item,
              product: {
                id: item.id,
                name: item.productName ?? '',
                price: item.price,
                originalPrice: item.originalPrice,
                categories: [] as I_Category[],
                amount: `${item.amount}`,
                parks: [item.park],
                park: item.park,
                image: item.park?.image ?? '',
                versionB2b: item?.product?.versionB2b,
                usedArea2Object: item?.product?.usedArea2Object,
              },
              amount: `${item.amount ?? 0}`,
              date: item.date?.dateDMY ?? '',
              zone: item.zone,
              show: item.show,
              price: item.price,
              originalPrice: item.originalPrice,
              performance: { ...item.performance, show: item.show },
              park: item.park,
              catalog: item.catalog,
              isGift: true,
              selectedDate: item.selectedDate?.dateDMY ?? '',
              higherLevelItem: item.higherLevelItem ?? '',
            })) ?? [];
        const vouchersData: Partial<I_CheckoutCartItem>[] | any =
          detailOrder?.giftItems
            ?.filter(val => val.materialType === MATERRIAL_EVOUCHER_TYPE)
            .map(item => ({
              ...item,
              product: {
                id: item.id,
                name: item.productName ?? '',
                price: item.price,
                originalPrice: item.originalPrice,
                categories: [] as I_Category[],
                amount: `${item.amount}`,
                parks: [item.park],
                park: item.park,
                image: item.park?.image ?? '',
                versionB2b: item?.product?.versionB2b,
                usedArea2Object: item?.product?.usedArea2Object,
              },
              amount: `${item.amount ?? 0}`,
              date: item.date?.dateDMY ?? '',
              zone: item.zone,
              show: item.show,
              price: item.price,
              originalPrice: item.originalPrice,
              performance: { ...item.performance, show: item.show },
              park: item.park,
              catalog: item.catalog,
              isGift: true,
            })) ?? [];
        detailOrder?.items?.forEach((item, indx) => {
          item.dataBuyer?.forEach((buyer, idxBuyer) => {
            formInfoUser.setFieldValue(
              `contacts[${item.id}][${idxBuyer}][identity_card]`,
              buyer.identityCode ?? '',
            );
            formInfoUser.setFieldValue(
              `contacts[${item.id}][${idxBuyer}][email]`,
              buyer.email ?? '',
            );
          });
        });
        if (detailOrder.mailTaxs && detailOrder.mailTaxs.length > 0) {
          const mailArrays = detailOrder?.mailTaxs.flatMap((item, idx) => {
            return {
              id: generateUUID(),
              initValue: item ?? '',
            };
          });
          setMailReceiveInvoices(mailArrays);
        }

        const groupDatas = Object.values(groupItems([...items, ...giftItems]));
        setVouchers(vouchersData);
        setGroupCart(groupDatas);
        setListCart([...items, ...giftItems]);
        // sessionStorage.removeItem(TYPE_LOCAL_STORAGE.ORDER);
      } catch (error: AxiosError | any) {
        const code =
          error?.response?.data?.code || error?.code || error?.status;
        if (code === 400 || code === 403) {
          history.push('/page-not-found');
        }
      } finally {
        setLoading(false);
      }
    };
    getDetail();
  }, [location, infoUser]);

  return useMemo(
    () => ({
      loading,
      INIT_FORM,
      onSubmitForm,
      listTicket,
      // listCartSelectedToCheckout,
      totalAmount,
      totalPrice,
      totalPriceOriginal,
      orderCheckout,
      enableConfirm,
      setEnableConfirm,
      totalDiscount,
      groupCart,
      infoUser,
      businessPartner,
      setLoading,
      vouchers,
      listCart,
      idOrder,
      mailTaxs,
      mailReceiveInvoices,
      setMailReceiveInvoices,
      formInfoUser,
      orderState,
      representativesInfo,
      orderDetail,
    }),
    [
      loading,
      INIT_FORM,
      onSubmitForm,
      // listCartSelectedToCheckout,
      listTicket,
      mailTaxs,
      totalAmount,
      totalPrice,
      totalPriceOriginal,
      orderCheckout,
      enableConfirm,
      setEnableConfirm,
      totalDiscount,
      groupCart,
      infoUser,
      businessPartner,
      setLoading,
      vouchers,
      listCart,
      idOrder,
      mailReceiveInvoices,
      setMailReceiveInvoices,
      formInfoUser,
      orderState,
      representativesInfo,
      orderDetail,
    ],
  );
};
